/**
 * IntegrationsContext
 * ─────────────────────────────────────────────────────────────
 * Central store for all third-party integration settings.
 * Admin can enable/disable platforms and manage API keys.
 * Settings persist in localStorage (replace with API in production).
 *
 * Consumed by:
 *   - AuthModal.js       → show/hide social login buttons
 *   - BookingPage.js     → show/hide meeting platforms
 *   - MeetingJoinButton  → show correct platform join button
 *   - AdminDashboard     → manage all settings
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// ── Default config ────────────────────────────────────────────
const DEFAULT_MEETING_PLATFORMS = [
  {
    id:          'google_meet',
    label:       'Google Meet',
    icon:        '🎥',
    color:       '#1a73e8',
    bgColor:     '#e8f0fe',
    enabled:     true,
    placeholder: 'https://meet.google.com/abc-defg-hij',
    hint:        'Free for everyone · No app install needed',
    config: {
      // TODO §M1: Add Google Client ID for auto-creating Meet links
      // Get from: console.cloud.google.com → APIs & Services → Credentials
      googleClientId:     '',
      googleClientSecret: '',
      autoCreateEnabled:  false,
    },
    status: 'active', // active | coming_soon | disabled
  },
  {
    id:          'zoom',
    label:       'Zoom',
    icon:        '📹',
    color:       '#2D8CFF',
    bgColor:     '#e6f2ff',
    enabled:     true,
    placeholder: 'https://zoom.us/j/123456789?pwd=xxxx',
    hint:        'Best for recordings and breakout rooms',
    config: {
      // TODO §M2: Add Zoom SDK credentials
      // Get from: marketplace.zoom.us → Develop → Build App → Meeting SDK
      zoomSdkKey:      '',
      zoomSdkSecret:   '',
      zoomAccountId:   '',
      zoomClientId:    '',
      zoomClientSecret:'',
      autoCreateEnabled: false,
      sdkEmbedEnabled:   false,
    },
    status: 'active',
  },
  {
    id:          'teams',
    label:       'Microsoft Teams',
    icon:        '🟣',
    color:       '#5059C9',
    bgColor:     '#edecfa',
    enabled:     true,
    placeholder: 'https://teams.microsoft.com/l/meetup-join/...',
    hint:        'Best for corporate environments',
    config: {
      // TODO §M3: Add Azure AD credentials for auto-creating Teams meetings
      // Get from: portal.azure.com → Azure AD → App registrations
      azureClientId:    '',
      azureTenantId:    '',
      azureClientSecret:'',
      autoCreateEnabled: false,
    },
    status: 'active',
  },
  {
    id:          'custom',
    label:       'Custom Link',
    icon:        '🔗',
    color:       '#374151',
    bgColor:     '#f3f4f6',
    enabled:     true,
    placeholder: 'https://your-meeting-link.com',
    hint:        'Skype, Jitsi, Whereby, or any other tool',
    config: {},
    status: 'active',
  },
];

const DEFAULT_SOCIAL_PROVIDERS = [
  {
    id:      'google',
    label:   'Google',
    icon:    '🔵',
    color:   '#4285F4',
    bgColor: '#e8f0fe',
    enabled: true,
    config: {
      // TODO §S1: Add Google OAuth credentials
      // Get from: console.cloud.google.com → APIs & Services → Credentials → OAuth 2.0
      clientId:     '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',
      clientSecret: '217656001460-qvhr3k26taufus2lg9rscsvpjm8ev0ml.apps.googleusercontent.com',
    },
    // Tells the UI if credentials are actually configured
    get isConfigured() { return !!(this.config.clientId); },
    notes: 'Requires Google Cloud Console OAuth 2.0 credentials.',
    docsUrl: 'https://console.cloud.google.com/',
  },
  {
    id:      'github',
    label:   'GitHub',
    icon:    '🐙',
    color:   '#24292e',
    bgColor: '#f6f8fa',
    enabled: true,
    config: {
      // TODO §S2: Add GitHub OAuth credentials
      // Get from: github.com/settings/developers → OAuth Apps → New OAuth App
      clientId:     'Ov23liYZ4hOfZ4NjAQgF',
      clientSecret: 'Bd0cbb6aeb85a1b43990d5daf7060035b2394b3d',
    },
    get isConfigured() { return !!(this.config.clientId); },
    notes: 'Create a GitHub OAuth App for each environment (dev + prod).',
    docsUrl: 'https://github.com/settings/developers',
  },
  {
    id:      'linkedin',
    label:   'LinkedIn',
    icon:    '💼',
    color:   '#0A66C2',
    bgColor: '#e8f3ff',
    enabled: true,
    config: {
      // TODO §S3: Add LinkedIn OAuth credentials
      // Get from: linkedin.com/developers/apps → New App → Auth tab
      clientId:     '',
      clientSecret: '',
    },
    get isConfigured() { return !!(this.config.clientId); },
    notes: 'Requires "Sign In with LinkedIn using OpenID Connect" product.',
    docsUrl: 'https://www.linkedin.com/developers/apps',
  },
];

// ── Context ───────────────────────────────────────────────────
const IntegrationsContext = createContext(null);

// ── Storage key ───────────────────────────────────────────────
const STORAGE_KEY = 'db_integrations_config';

// ── Load from localStorage ────────────────────────────────────
function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// ── Provider ──────────────────────────────────────────────────
export function IntegrationsProvider({ children }) {
  const saved = loadSaved();

  const [meetingPlatforms, setMeetingPlatforms] = useState(
    saved?.meetingPlatforms || DEFAULT_MEETING_PLATFORMS
  );
  const [socialProviders, setSocialProviders] = useState(
    saved?.socialProviders || DEFAULT_SOCIAL_PROVIDERS
  );
  const [lastSaved, setLastSaved] = useState(null);

  // ── Persist on change ─────────────────────────────────────
  useEffect(() => {
    try {
      // ⚠️  In production: never store secrets in localStorage
      //     Store only the enabled flags here; keep secrets server-side
      //     TODO: Replace with PUT /api/admin/integrations
      const toStore = {
        meetingPlatforms: meetingPlatforms.map(p => ({
          id: p.id, enabled: p.enabled,
          // ⚠️  Strip secrets before storing in localStorage
          config: Object.fromEntries(
            Object.entries(p.config).filter(([k]) => !k.toLowerCase().includes('secret'))
          ),
        })),
        socialProviders: socialProviders.map(p => ({
          id: p.id, enabled: p.enabled,
          config: { clientId: p.config.clientId }, // never store secret locally
        })),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch {}
  }, [meetingPlatforms, socialProviders]);

  // ── Meeting platform helpers ──────────────────────────────
  const toggleMeetingPlatform = useCallback((id) => {
    setMeetingPlatforms(prev =>
      prev.map(p => p.id === id ? { ...p, enabled: !p.enabled } : p)
    );
  }, []);

  const updateMeetingConfig = useCallback((id, configUpdates) => {
    setMeetingPlatforms(prev =>
      prev.map(p => p.id === id ? { ...p, config: { ...p.config, ...configUpdates } } : p)
    );
  }, []);

  const getMeetingPlatform = useCallback((id) =>
    meetingPlatforms.find(p => p.id === id), [meetingPlatforms]
  );

  const getEnabledMeetingPlatforms = useCallback(() =>
    meetingPlatforms.filter(p => p.enabled), [meetingPlatforms]
  );

  // ── Social provider helpers ───────────────────────────────
  const toggleSocialProvider = useCallback((id) => {
    setSocialProviders(prev =>
      prev.map(p => p.id === id ? { ...p, enabled: !p.enabled } : p)
    );
  }, []);

  const updateSocialConfig = useCallback((id, configUpdates) => {
    setSocialProviders(prev =>
      prev.map(p => p.id === id ? { ...p, config: { ...p.config, ...configUpdates } } : p)
    );
  }, []);

  const getSocialProvider = useCallback((id) =>
    socialProviders.find(p => p.id === id), [socialProviders]
  );

  const isSocialEnabled = useCallback((id) => {
    const p = socialProviders.find(pr => pr.id === id);
    return p?.enabled ?? false;
  }, [socialProviders]);

  const isMeetingEnabled = useCallback((id) => {
    const p = meetingPlatforms.find(pl => pl.id === id);
    return p?.enabled ?? false;
  }, [meetingPlatforms]);

  // ── Save handler (admin panel) ────────────────────────────
  const saveAll = useCallback(() => {
    setLastSaved(new Date());
    // TODO: POST to /api/admin/integrations
  }, []);

  const resetAll = useCallback(() => {
    setMeetingPlatforms(DEFAULT_MEETING_PLATFORMS);
    setSocialProviders(DEFAULT_SOCIAL_PROVIDERS);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <IntegrationsContext.Provider value={{
      // Meeting
      meetingPlatforms,
      toggleMeetingPlatform,
      updateMeetingConfig,
      getMeetingPlatform,
      getEnabledMeetingPlatforms,
      isMeetingEnabled,
      // Social
      socialProviders,
      toggleSocialProvider,
      updateSocialConfig,
      getSocialProvider,
      isSocialEnabled,
      // Admin
      saveAll,
      resetAll,
      lastSaved,
    }}>
      {children}
    </IntegrationsContext.Provider>
  );
}

export function useIntegrations() {
  return useContext(IntegrationsContext);
}
